import React, { useContext } from 'react';
import { graphql } from 'gatsby';
import ProductSupportLayout from './ProductSupportLayout';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { Faq as FaqType } from '../Global/DataUtils/faqQueries';
import { extractDocuments } from '../GuidesAndManuals/index';
import {
    QuickTile,
    SupportVideo,
    SupportDocument,
    DocumentGroup,
} from './productSupportTypes';
import {
    extractDocImage,
    extractQuickTiles,
    extractVideos,
} from './productSupportUtils';
import {
    KontentNumberValue,
    KontentStringValue,
} from '../../../types/KontentProps';

interface Props {
    anchor: KontentStringValue;
    headline?: KontentStringValue;
    max_number_to_display?: KontentNumberValue;
    product: {
        value: {
            elements: {
                associated_product_support_page: {
                    value: {
                        elements: {
                            slug: {
                                value: string;
                            };
                        };
                        id: string;
                    }[];
                };
                documents: {
                    value: SupportDocument[];
                };
                document_group: {
                    value: DocumentGroup[];
                };
                quick_tiles: {
                    value: QuickTile[];
                };
                videos: {
                    value: SupportVideo[];
                };
                faqs: {
                    value: FaqType[];
                };
            };
        }[];
    };
    product_0acaef2: {
        value: {
            elements: {
                display_page_name: {
                    value: string;
                };
            };
        }[];
    };
    kontentItemId?: string;
}

const ProductSupportLayoutContainer: React.FC<Props> = ({
    product,
    product_0acaef2,
    anchor,
    headline,
    max_number_to_display,
    kontentItemId,
}) => {
    const pageMetadata = useContext(PageMetadataContext);
    const documents = extractDocuments(
        product?.value[0]?.elements?.documents?.value
    );
    const documentGroups =
        product?.value[0]?.elements?.document_group?.value || [];
    const productName =
        product_0acaef2?.value[0]?.elements?.display_page_name?.value;
    const documentImage = extractDocImage(documents);
    const quickTiles = extractQuickTiles(
        product?.value[0]?.elements?.quick_tiles?.value
    );
    const videos = extractVideos(product?.value[0]?.elements?.videos?.value);

    return (
        <ProductSupportLayout
            pageMetaData={pageMetadata}
            productName={productName}
            documentImage={documentImage}
            documents={documents}
            documentGroups={documentGroups}
            quickTiles={quickTiles}
            videos={videos}
            faqs={product?.value[0]?.elements?.faqs?.value}
            faqanchor={anchor?.value}
            maxQuestionToDisplay={max_number_to_display?.value}
            headline={headline?.value}
            kontentItemId={kontentItemId}
        />
    );
};

export default ProductSupportLayoutContainer;

export const fragmentProductSupportLayout = graphql`
    fragment ProductSupportLayout on kontent_item_component___product_support_layout {
        id
        elements {
            anchor {
                value
            }
            max_number_to_display {
                value
            }
            headline {
                value
            }
            product {
                value {
                    ... on kontent_item_data___product {
                        elements {
                            documents {
                                value {
                                    ... on kontent_item_documents___products {
                                        elements {
                                            document_type {
                                                value {
                                                    codename
                                                    name
                                                }
                                            }
                                            file {
                                                value {
                                                    url
                                                    description
                                                }
                                            }
                                            image {
                                                value {
                                                    ... on kontent_item_media___image {
                                                        elements {
                                                            file {
                                                                value {
                                                                    url
                                                                    description
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        system {
                                            name
                                        }
                                    }
                                }
                            }
                            document_group {
                                value {
                                    ... on kontent_item_data___document_group {
                                        id
                                        elements {
                                            title {
                                                value
                                            }
                                            documents {
                                                value {
                                                    ... on kontent_item_documents___products {
                                                        id
                                                        elements {
                                                            display_on_product_detail_page {
                                                                value {
                                                                    codename
                                                                    name
                                                                }
                                                            }
                                                            document_type {
                                                                value {
                                                                    codename
                                                                    name
                                                                }
                                                            }
                                                            image {
                                                                ...kontentImage
                                                            }
                                                            file {
                                                                value {
                                                                    url
                                                                    description
                                                                }
                                                            }
                                                            pdf_embedded_links {
                                                                ...kontentRichText
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            faqs {
                                value {
                                    ... on kontent_item_content_item___faq {
                                        ...kontentFaq
                                    }
                                }
                            }
                            quick_tiles {
                                value {
                                    ... on kontent_item_content_item___support_quick_tile {
                                        elements {
                                            title {
                                                value
                                            }
                                            asset {
                                                value {
                                                    ... on kontent_item_media___icon {
                                                        elements {
                                                            ...kontentIcon
                                                        }
                                                    }
                                                    ... on kontent_item_media___image {
                                                        id
                                                        elements {
                                                            file {
                                                                value {
                                                                    url
                                                                    description
                                                                    name
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                            linked_items {
                                                value {
                                                    ... on kontent_item_documents___products {
                                                        elements {
                                                            file {
                                                                value {
                                                                    url
                                                                }
                                                            }
                                                        }
                                                    }
                                                    ... on kontent_item_layout___master_page {
                                                        id
                                                        elements {
                                                            slug {
                                                                value
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            videos {
                                value {
                                    ... on kontent_item_media___product_support_video {
                                        elements {
                                            youtube_url {
                                                value
                                            }
                                            caption {
                                                value
                                            }
                                            image {
                                                value {
                                                    ... on kontent_item_media___image {
                                                        elements {
                                                            file {
                                                                value {
                                                                    url
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            product_0acaef2 {
                value {
                    ... on kontent_item_layout___master_page {
                        elements {
                            display_page_name {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
